import {
  normalizeGraphLayout,
  RELATIONSHIP_GRAPH_SCHEMA_VERSION,
  type GraphDocument,
  type RelationshipPair,
} from '../../entities/graph-document';
import type { JourneyNode } from '../../entities/node';
import {
  AI_ASSIST_CONFIG_SCHEMA_VERSION,
  createDefaultGraphAIAssistConfig,
  type GraphAIAssistConfig,
} from '../../entities/ai-assist-config';
import {
  GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION,
  createDefaultGraphAppearanceConfig,
  normalizePersistedGraphNodeTypeVisualConfig,
  type GraphAppearanceConfig,
} from '../../entities/graph-appearance-config';
import type { MediaManifest } from '../../entities/media-asset';
import type { ArchiveValidationResult, StoragePreflightResult } from '../../../types/archive';
import { estimateNodeAnchorDepths } from '../workspace/depth-estimation';
import { validateExplicitRelationshipPair, validateRelationshipsCollection } from '../explicit-relationships';

function isNode(value: unknown): value is JourneyNode { return !!value && typeof value === 'object' && ['Human', 'Project', 'Occupation'].includes((value as { type?: string }).type ?? ''); }
export function validateGraphDocument(graph: unknown, mediaManifest?: MediaManifest): ArchiveValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const g = graph as Partial<GraphDocument> | null;
  if (!g || typeof g !== 'object') errors.push('graph.json must be an object');
  if (g?.schemaVersion !== RELATIONSHIP_GRAPH_SCHEMA_VERSION) errors.push('unsupported graph schemaVersion');
  if (!g?.graphId) errors.push('graphId is required');
  if (!g?.title) errors.push('title is required');
  if (!Array.isArray(g?.nodes)) errors.push('nodes array is required');
  if (!Array.isArray(g?.connections)) errors.push('connections array is required');
  const relationshipsValidation = validateRelationshipsCollection(g?.relationships);
  if (!relationshipsValidation.valid) errors.push(...relationshipsValidation.errors);
  if (g?.layout !== undefined && (!g.layout || typeof g.layout !== 'object' || Array.isArray(g.layout))) errors.push('layout must be an object');
  const nodes = Array.isArray(g?.nodes) ? g.nodes.filter(isNode) : [];
  if (Array.isArray(g?.nodes) && nodes.length !== g.nodes.length) errors.push('all nodes must use a built-in node type');
  const ids = new Set(nodes.map((node) => node.nodeId));
  if (ids.size !== nodes.length) errors.push('nodeId must be unique');
  const rawNodeAnchors = g?.layout && typeof g.layout === 'object' && !Array.isArray(g.layout) && 'nodeAnchors' in g.layout
    ? (g.layout as { nodeAnchors?: Record<string, { x?: unknown; y?: unknown; z?: unknown }> }).nodeAnchors
    : undefined;
  if (rawNodeAnchors !== undefined && (!rawNodeAnchors || typeof rawNodeAnchors !== 'object' || Array.isArray(rawNodeAnchors))) errors.push('layout.nodeAnchors must be an object');
  const nodeIdsWithInvalidZ: string[] = [];
  for (const [nodeId, anchor] of Object.entries(rawNodeAnchors ?? {})) {
    if (typeof anchor?.x !== 'number' || typeof anchor?.y !== 'number') errors.push(`layout anchor for ${nodeId} must include numeric x and y`);
    if (anchor?.z !== undefined && typeof anchor.z !== 'number') {
      nodeIdsWithInvalidZ.push(nodeId);
      warnings.push(`layout anchor for ${nodeId} had an invalid z value and was re-estimated`);
    }
  }
  const relationships = relationshipsValidation.value ?? [];
  for (const relationshipPair of relationships) {
    const pairValidation = validateExplicitRelationshipPair(relationshipPair, ids);
    if (!pairValidation.valid) {
      errors.push(...pairValidation.errors);
    }
  }
  for (const connection of g?.connections ?? []) {
    if (!ids.has(connection.sourceNodeId) || !ids.has(connection.targetNodeId)) errors.push('connection references missing node');
    if (connection.strengthScore < 0 || connection.strengthScore > 1) errors.push('connection strengthScore must be between 0 and 1');
  }
  const manifestAssetIds = new Set((mediaManifest?.assets ?? []).map((asset) => asset.assetId));
  for (const node of nodes) if (node.imageSource === 'upload' && node.imageAssetId && !manifestAssetIds.has(node.imageAssetId)) warnings.push(`missing image asset for ${node.nodeId}; fallback will be used`);
  const normalizedLayout = normalizeGraphLayout(g?.layout);
  let filteredNodeAnchors = Object.fromEntries(Object.entries(normalizedLayout.nodeAnchors).filter(([nodeId]) => ids.has(nodeId)));
  if (Object.keys(filteredNodeAnchors).length !== Object.keys(normalizedLayout.nodeAnchors).length) warnings.push('layout anchors for missing nodes were ignored');
  const hasFlatDepth = Object.keys(filteredNodeAnchors).length > 1 && Object.values(filteredNodeAnchors).every((anchor) => anchor.z === 0);
  if (hasFlatDepth || nodeIdsWithInvalidZ.some((nodeId) => nodeId in filteredNodeAnchors)) {
    filteredNodeAnchors = estimateNodeAnchorDepths(filteredNodeAnchors, {
      forceReestimateAll: hasFlatDepth,
      nodeIdsNeedingDepth: nodeIdsWithInvalidZ.filter((nodeId) => nodeId in filteredNodeAnchors),
    });
  }
  if (hasFlatDepth) warnings.push('layout depth was flat and was re-estimated for 3D mode');
  const normalizedGraph = errors.length
    ? undefined
    : {
        ...g,
        schemaVersion: RELATIONSHIP_GRAPH_SCHEMA_VERSION,
        layout: { nodeAnchors: filteredNodeAnchors },
        relationships: normalizeRelationships(relationships),
      } as GraphDocument;
  return { valid: errors.length === 0, errors: [...new Set(errors)], warnings: [...new Set(warnings)], graph: normalizedGraph, mediaManifest };
}
export function validateMediaManifest(manifest: unknown): { valid: boolean; errors: string[]; manifest?: MediaManifest } {
  const m = manifest as Partial<MediaManifest> | null;
  const errors: string[] = [];
  if (!m || typeof m !== 'object') errors.push('media-manifest.json must be an object');
  if (m?.schemaVersion !== '1.0.0') errors.push('unsupported media manifest schemaVersion');
  if (!Array.isArray(m?.assets)) errors.push('assets array is required');
  return { valid: errors.length === 0, errors, manifest: errors.length ? undefined : m as MediaManifest };
}

export function validateAIAssistConfig(
  configJson: unknown,
  graph?: GraphDocument,
): { config: GraphAIAssistConfig; warnings: string[] } {
  const warnings: string[] = [];
  const defaultConfig = createDefaultGraphAIAssistConfig();
  if (!configJson || typeof configJson !== 'object') {
    warnings.push('AI settings were missing and were initialized with defaults.');
    return { config: defaultConfig, warnings };
  }

  const aiAssistConfig = (configJson as { aiAssistConfig?: unknown }).aiAssistConfig;
  if (!aiAssistConfig || typeof aiAssistConfig !== 'object') {
    warnings.push('Archive did not include AI settings; defaults were initialized.');
    return { config: defaultConfig, warnings };
  }

  const candidateConfig = aiAssistConfig as Partial<GraphAIAssistConfig>;
  if (candidateConfig.schemaVersion !== AI_ASSIST_CONFIG_SCHEMA_VERSION) {
    warnings.push('Unknown AI settings version. Safe defaults were initialized.');
    return { config: defaultConfig, warnings };
  }

  if (!candidateConfig.deIdentification || !candidateConfig.promptContract) {
    warnings.push('Incomplete AI settings were ignored and replaced with defaults.');
    return { config: defaultConfig, warnings };
  }

  if (!candidateConfig.createNodePromptContract) {
    warnings.push('Create-node AI prompt settings were missing and were initialized with defaults.');
  }

  const config: GraphAIAssistConfig = {
    schemaVersion: AI_ASSIST_CONFIG_SCHEMA_VERSION,
    deIdentification: {
      enabled: candidateConfig.deIdentification.enabled ?? defaultConfig.deIdentification.enabled,
      humanAliasPrefix: candidateConfig.deIdentification.humanAliasPrefix?.trim() || defaultConfig.deIdentification.humanAliasPrefix,
      candidates: Array.isArray(candidateConfig.deIdentification.candidates) ? candidateConfig.deIdentification.candidates : [],
      manualRules: Array.isArray(candidateConfig.deIdentification.manualRules) ? candidateConfig.deIdentification.manualRules : [],
      updatedAt: candidateConfig.deIdentification.updatedAt ?? defaultConfig.deIdentification.updatedAt,
    },
    promptContract: {
      ...defaultConfig.promptContract,
      ...candidateConfig.promptContract,
      responseEnvelope: 'operations',
      exampleResponse: candidateConfig.promptContract.exampleResponse ?? defaultConfig.promptContract.exampleResponse,
    },
    createNodePromptContract: {
      ...defaultConfig.createNodePromptContract,
      ...candidateConfig.createNodePromptContract,
      examplePayload: candidateConfig.createNodePromptContract?.examplePayload ?? defaultConfig.createNodePromptContract.examplePayload,
    },
  };

  const nodeIds = new Set(graph?.nodes.map((node) => node.nodeId) ?? []);
  if (graph) {
    config.deIdentification.candidates = config.deIdentification.candidates.map((candidate) => {
      const danglingOccurrences = candidate.occurrences?.filter((occurrence) => !nodeIds.has(occurrence.nodeId)) ?? [];
      if (danglingOccurrences.length === 0) {
        return candidate;
      }
      warnings.push(`Candidate "${candidate.originalValue}" references missing nodes and should be re-scanned.`);
      return {
        ...candidate,
        hasDanglingOccurrences: true,
      };
    });
  }

  const duplicateReplacementMap = new Map<string, Set<string>>();
  for (const rule of config.deIdentification.manualRules.filter((rule) => rule.enabled)) {
    const originals = duplicateReplacementMap.get(rule.replacementValue) ?? new Set<string>();
    originals.add(rule.originalValue);
    duplicateReplacementMap.set(rule.replacementValue, originals);
  }

  config.deIdentification.manualRules = config.deIdentification.manualRules.map((rule) => ({
    ...rule,
    conflictStatus: (duplicateReplacementMap.get(rule.replacementValue)?.size ?? 0) > 1 ? 'blocked-conflict' : (rule.conflictStatus ?? 'valid'),
  }));

  return { config, warnings: [...new Set(warnings)] };
}

export function validateGraphAppearanceConfig(
  configJson: unknown,
): { config: GraphAppearanceConfig; warnings: string[] } {
  const warnings: string[] = [];
  const defaultConfig = createDefaultGraphAppearanceConfig();
  if (!configJson || typeof configJson !== 'object') {
    return { config: defaultConfig, warnings };
  }

  const graphAppearanceConfig = (configJson as { graphAppearanceConfig?: unknown }).graphAppearanceConfig;
  if (!graphAppearanceConfig || typeof graphAppearanceConfig !== 'object') {
    return { config: defaultConfig, warnings };
  }

  const candidateConfig = graphAppearanceConfig as Partial<GraphAppearanceConfig>;
  if (candidateConfig.schemaVersion !== GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION) {
    warnings.push('Unknown graph appearance settings version. Default appearance settings were used.');
    return { config: defaultConfig, warnings };
  }

  if (!candidateConfig.nodeTypeVisuals || typeof candidateConfig.nodeTypeVisuals !== 'object' || Array.isArray(candidateConfig.nodeTypeVisuals)) {
    warnings.push('Invalid graph appearance settings were ignored and replaced with defaults.');
    return { config: defaultConfig, warnings };
  }

  const nodeTypeVisuals = Object.fromEntries(
    Object.entries(candidateConfig.nodeTypeVisuals)
      .filter(([, entry]) => entry && typeof entry === 'object' && !Array.isArray(entry))
      .map(([typeId, entry]) => [
        typeId,
        normalizePersistedGraphNodeTypeVisualConfig(typeId, entry as Parameters<typeof normalizePersistedGraphNodeTypeVisualConfig>[1]),
      ]),
  ) as GraphAppearanceConfig['nodeTypeVisuals'];

  if (Object.keys(nodeTypeVisuals).length !== Object.keys(candidateConfig.nodeTypeVisuals).length) {
    warnings.push('Some graph appearance entries were invalid and were reset to defaults.');
  }

  return {
    config: {
      schemaVersion: GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION,
      nodeTypeVisuals,
    },
    warnings: [...new Set(warnings)],
  };
}

export function estimateArchivePreflight(graph: GraphDocument, assetsBytes = 0, availableBytes = Number.POSITIVE_INFINITY): StoragePreflightResult {
  const estimatedBytes = new Blob([JSON.stringify(graph)]).size + assetsBytes;
  const warnings = estimatedBytes > 50 * 1024 * 1024 ? ['Large archive may take longer than expected'] : [];
  return { canProceed: estimatedBytes < availableBytes, estimatedBytes, warnings };
}

function normalizeRelationships(relationships: RelationshipPair[]): RelationshipPair[] {
  return relationships.map((relationshipPair) => ({
    ...relationshipPair,
    endpoints: [...relationshipPair.endpoints] as RelationshipPair['endpoints'],
    directions: relationshipPair.directions.map((direction) => ({
      ...direction,
      labels: [...direction.labels],
    })) as RelationshipPair['directions'],
  }));
}
